import type { ClientConsentKind, ClientConsentView } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useState } from "react"

import { grantClientConsent, revokeClientConsent } from "../../../api/clients.api"
import { isApiProblem } from "../../../api/problem"
import { JournalDialog } from "../../../journal/appointment-dialogs"
import { CONSENT_PERSONAL_DATA_VERSION } from "../../../journal/booking-draft"
import { cx } from "../../../lib/cx"
import { todayInZone } from "../../../lib/format"
import { Badge, Button, SelectField, TextField } from "../../../ui"
import {
  CONSENT_TITLE,
  type ConsentLine,
  type ConsentState,
  OTHER_KINDS,
  STATE_LABEL,
  adsHint,
  dateOf,
  freshClientRevocation,
  grantedAtOf,
  grantedVia,
  lineOf,
  otherSubOf,
  paperProblem,
  revocableKinds,
  revokedVia,
  subOf,
  versionLabel,
} from "./consents-model"
import styles from "./consents-tab.module.css"

/**
 * 3-А5 «Согласия» — вкладка карточки клиента (ТЗ 03, А5; макет П2). Видит тот, у кого
 * право clients.consents.manage: администратор и управляющий, мастер — нет.
 *
 * ПОЧЕМУ ДВА ГЛАВНЫХ БЛОКА ОФОРМЛЕНЫ ПО-РАЗНОМУ. Обработка данных — основание вообще вести
 * карточку, реклама — отдельный документ, и рассылка без него — штраф салону. Две
 * одинаковые галочки рядом — ровно та ошибка, ради которой экран существует.
 *
 * ТЕКСТ РЕДАКЦИИ И ФОРМА ДЛЯ КЛИЕНТА ЗАКРЫТЫ С ОБЪЯСНЕНИЕМ: самих текстов в программе
 * ещё нет (решение владельца №3 — кто их хранит и правит), а форма без текста — пустая
 * ссылка. Номер редакции при этом виден всегда: «на что именно он соглашался» — вопрос,
 * ради которого журнал и ведётся.
 */
export function ConsentsTab(props: {
  readonly clientId: string
  readonly clientName: string
  readonly consents: readonly ClientConsentView[]
  readonly hasTelegram: boolean
  readonly timeZone: string
  readonly onChanged: () => void
}): ReactNode {
  const [dialog, setDialog] = useState<"grant" | "revoke" | null>(null)
  const [outcome, setOutcome] = useState<string | null>(null)

  const pd = lineOf(props.consents, "pd")
  const ads = lineOf(props.consents, "ads")
  const others = OTHER_KINDS.map((kind) => lineOf(props.consents, kind))
  const revocable = revocableKinds(props.consents)
  const empty = props.consents.length === 0

  return (
    <div className={styles.panel}>
      <div>
        <div className={styles.title}>{props.clientName} · согласия</div>
        <div className={styles.muted}>
          Журнал не стирается: отзыв — новая отметка, а не удаление прежней. Отзыв действует немедленно.
        </div>
      </div>

      {freshClientRevocation(ads, new Date()) ? (
        <div className={styles.alert} role="alert">
          Клиент только что отозвал согласие на рекламу — сам, по ссылке отписки или в чате. Отправлять ему
          акции больше нельзя.
        </div>
      ) : null}

      {empty ? (
        <div className={styles.notice}>
          Согласий не зафиксировано. При следующей записи администратор отметит согласие на обработку данных —
          без него запись не сохранится. Отправлять акции нельзя до отдельного рекламного согласия.
        </div>
      ) : null}

      {outcome === null ? null : (
        <div className={styles.notice} role="status">
          {outcome}
        </div>
      )}

      <section className={styles.basis} aria-label="Основание вести карточку">
        <span className={styles.kicker}>Основание вести карточку</span>
        <MainRow
          line={pd}
          title={CONSENT_TITLE.pd}
          note="Без него салону нельзя вести этого клиента. Требуется при каждой записи."
          timeZone={props.timeZone}
        />
        <div className={styles.meta}>
          <VersionMeta line={pd} />
          {pd.current?.paper === true && pd.state === "given" ? (
            <span className={styles.paper}>Принято на бумаге — бумага должна лежать у салона</span>
          ) : null}
        </div>
        <History line={pd} timeZone={props.timeZone} />
      </section>

      <section className={styles.ads} aria-label="Рекламные сообщения">
        <span className={styles.kicker}>Отдельный документ — рекламные сообщения</span>
        <MainRow
          line={ads}
          title={CONSENT_TITLE.ads}
          note="Акции, поздравления, «давно не были». Без него — только напоминания о его же записях."
          timeZone={props.timeZone}
        />
        <div className={styles.meta}>
          {ads.current === null ? null : <VersionMeta line={ads} />}
          <span className={stateClass(ads.state)}>{adsHint(ads.state)}</span>
          {ads.current?.paper === true && ads.state === "given" ? (
            <span className={styles.paper}>Принято на бумаге — бумага должна лежать у салона</span>
          ) : null}
        </div>
        <History line={ads} timeZone={props.timeZone} />
      </section>

      <div className={styles.others}>
        {others.map((line) => (
          <div key={line.kind} className={styles.other}>
            <div className={styles.otherHead}>
              <span className={styles.name}>{CONSENT_TITLE[line.kind]}</span>
              <span className={cx(styles.state, stateClass(line.state))}>{STATE_LABEL[line.state]}</span>
            </div>
            <div className={styles.sub}>{otherSubOf(line, props.timeZone)}</div>
          </div>
        ))}
      </div>

      <div className={styles.legend}>
        <span>
          <b className={styles.given}>● Дано</b> — дата, источник, редакция
        </span>
        <span>
          <b className={styles.revoked}>✕ Отозвано</b> — повторно не спрашиваем без повода
        </span>
        <span>
          <b className={styles.never}>○ Не запрашивалось</b> — надо спросить; это не отказ
        </span>
      </div>

      <div className={styles.actions}>
        <Button variant="primary" size="sm" onClick={() => setDialog("grant")}>
          Отметить: получено на бумаге…
        </Button>
        <Button
          size="sm"
          deniedReason="Форма согласия появится вместе с текстами редакций в программе. Пока — бумага у стойки"
        >
          {props.hasTelegram ? "Отправить форму согласия в Telegram" : "Отправить форму согласия"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          deniedReason={revocable.length === 0 ? "Действующих согласий нет — отзывать нечего" : undefined}
          onClick={() => setDialog("revoke")}
        >
          Зафиксировать отзыв по требованию клиента
        </Button>
      </div>
      <div className={styles.muted}>
        Отметка бумажного согласия без редакции текста не сохраняется: без неё отметка не будет иметь силы при
        проверке.
      </div>

      {dialog === "grant" ? (
        <PaperDialog
          clientId={props.clientId}
          timeZone={props.timeZone}
          onClose={() => setDialog(null)}
          onDone={(kind) => {
            setDialog(null)
            setOutcome(`Отмечено: «${CONSENT_TITLE[kind]}» получено на бумаге.`)
            props.onChanged()
          }}
        />
      ) : null}
      {dialog === "revoke" ? (
        <RevokeDialog
          clientId={props.clientId}
          kinds={revocable}
          onClose={() => setDialog(null)}
          onDone={(kind, requiresErasure) => {
            setDialog(null)
            setOutcome(
              requiresErasure
                ? "Согласие на обработку данных отозвано. Данные клиента при этом НЕ удалены: удаление — отдельное действие, «Удалить данные клиента»."
                : `Отзыв «${CONSENT_TITLE[kind]}» зафиксирован и уже действует.`,
            )
            props.onChanged()
          }}
        />
      ) : null}
    </div>
  )
}

function stateClass(state: ConsentState): string | undefined {
  return state === "given" ? styles.given : state === "revoked" ? styles.revoked : styles.never
}

function MainRow(props: {
  readonly line: ConsentLine
  readonly title: string
  readonly note: string
  readonly timeZone: string
}): ReactNode {
  return (
    <div className={styles.row}>
      <div className={styles.grow}>
        <div className={styles.name}>{props.title}</div>
        <div className={styles.muted}>{props.note}</div>
      </div>
      <div className={styles.stateCol}>
        <div className={cx(styles.state, stateClass(props.line.state))}>{STATE_LABEL[props.line.state]}</div>
        <div className={styles.sub}>{subOf(props.line, props.timeZone)}</div>
      </div>
    </div>
  )
}

function VersionMeta(props: { readonly line: ConsentLine }): ReactNode {
  const consent = props.line.current
  if (consent === null) return <span className={styles.sub}>Редакции нет — согласие не давалось</span>
  return (
    <>
      <Badge tone="gray">{versionLabel(consent.version)}</Badge>
      <button
        type="button"
        className={styles.textLink}
        disabled
        title="Тексты редакций в программу ещё не загружены — подписанный экземпляр хранится у салона"
      >
        Открыть текст этой редакции
      </button>
    </>
  )
}

/** История вида — под раскрытием: текущее крупно, «дал — отозвал — дал снова» ниже. */
function History(props: { readonly line: ConsentLine; readonly timeZone: string }): ReactNode {
  const [open, setOpen] = useState(false)
  if (props.line.history.length < 2) return null
  return (
    <div>
      <button type="button" className={styles.textLink} aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? "Скрыть историю" : `История · ${props.line.history.length}`}
      </button>
      {open ? (
        <ul className={styles.history}>
          {props.line.history.map((item, index) => (
            <li key={`${item.grantedAt}-${index}`}>
              дано {dateOf(item.grantedAt, props.timeZone)} · {grantedVia(item)} · {versionLabel(item.version)}
              {item.revokedAt === null
                ? " — действует"
                : ` — отозвано ${dateOf(item.revokedAt, props.timeZone)}, ${revokedVia(item)}`}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

const ALL_KINDS: readonly ClientConsentKind[] = ["pd", "ads", ...OTHER_KINDS]

function PaperDialog(props: {
  readonly clientId: string
  readonly timeZone: string
  readonly onClose: () => void
  readonly onDone: (kind: ClientConsentKind) => void
}): ReactNode {
  const today = todayInZone(props.timeZone)
  const [kind, setKind] = useState<ClientConsentKind>("pd")
  const [version, setVersion] = useState(CONSENT_PERSONAL_DATA_VERSION)
  const [date, setDate] = useState(today)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const submit = async (): Promise<void> => {
    const local = paperProblem({ version, date, today })
    if (local !== null) {
      setProblem(local)
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      const grantedAt = grantedAtOf(date, today)
      await grantClientConsent({
        clientId: props.clientId,
        kind,
        version: version.trim(),
        ...(grantedAt === undefined ? {} : { grantedAt }),
      })
      props.onDone(kind)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title="Согласие получено на бумаге" onClose={props.onClose}>
      <div className={styles.dialogBody}>
        <SelectField
          label="Какое согласие"
          value={kind}
          options={ALL_KINDS.map((value) => ({ value, label: CONSENT_TITLE[value] }))}
          onChange={(event) => {
            const next = event.target.value as ClientConsentKind
            setKind(next)
            // Редакция ПД известна кабинету; для остальных — вписать с бумаги, без подсказки,
            // чтобы чужая редакция не легла в журнал по инерции.
            setVersion(next === "pd" ? CONSENT_PERSONAL_DATA_VERSION : "")
            setProblem(null)
          }}
        />
        {kind === "ads" ? (
          <p className={styles.muted}>
            Рекламное согласие — отдельный документ. Отмечайте, только если клиент подписал именно его, а не
            согласие на обработку данных.
          </p>
        ) : null}
        <TextField
          label="Редакция текста"
          hint="Как написано на бумаге: дата редакции или её номер"
          value={version}
          maxLength={32}
          onChange={(event) => {
            setVersion(event.target.value)
            setProblem(null)
          }}
        />
        <TextField
          label="Когда подписано"
          type="date"
          value={date}
          max={today}
          onChange={(event) => {
            setDate(event.target.value)
            setProblem(null)
          }}
        />
        <p className={styles.muted}>
          Отметка запишется от вашего имени, источник — «администратор». Подписанную бумагу сохраните в салоне: она и
          есть доказательство.
        </p>
        {problem === null ? null : (
          <div className={styles.alert} role="alert">
            {problem}
          </div>
        )}
        <div className={styles.dialogButtons}>
          <Button onClick={props.onClose}>Отмена</Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            Отметить
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}

function RevokeDialog(props: {
  readonly clientId: string
  readonly kinds: readonly ClientConsentKind[]
  readonly onClose: () => void
  readonly onDone: (kind: ClientConsentKind, requiresErasure: boolean) => void
}): ReactNode {
  const [kind, setKind] = useState<ClientConsentKind>(props.kinds.includes("ads") ? "ads" : (props.kinds[0] ?? "ads"))
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const submit = async (): Promise<void> => {
    setBusy(true)
    setProblem(null)
    try {
      const result = await revokeClientConsent({ clientId: props.clientId, kind })
      props.onDone(kind, result.requiresErasure)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title="Отзыв согласия по требованию клиента" onClose={props.onClose}>
      <div className={styles.dialogBody}>
        <SelectField
          label="Что отзывает клиент"
          value={kind}
          options={props.kinds.map((value) => ({ value, label: CONSENT_TITLE[value] }))}
          onChange={(event) => {
            setKind(event.target.value as ClientConsentKind)
            setProblem(null)
          }}
        />
        <p className={styles.muted}>
          Отзыв действует немедленно: уже собранные рассылки этому клиенту не уйдут. Строка журнала останется —
          отзыв ложится поверх неё.
        </p>
        {kind === "pd" ? (
          <p className={styles.muted}>
            Отзыв согласия на обработку данных не удаляет карточку. Удалить данные клиента — отдельное действие с
            отдельным правом.
          </p>
        ) : null}
        {problem === null ? null : (
          <div className={styles.alert} role="alert">
            {problem}
          </div>
        )}
        <div className={styles.dialogButtons}>
          <Button onClick={props.onClose}>Отмена</Button>
          <Button variant="danger" loading={busy} onClick={() => void submit()}>
            Зафиксировать отзыв
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}
